import { FC } from 'react'

import styled from 'styled-components'

import Trash from 'src/Assets/trash.svg?react'
import { colors, device } from 'src/Helpers/constants.ts'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { Button } from 'src/Components/Button'
import { Badge } from 'src/Components/Badge'
import { getValueOrFallback } from 'src/Helpers/functions'
import { ManagedUser, UserStatus } from 'src/Redux/api/usersApi.ts'

import { roleLabels } from './constants.ts'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  gap: 10px;
  background-color: ${colors.mainGray};
  padding: 10px;

  @media ${device.tabletMax} {
    padding: 20px;
  }
`

const ParametersContainer = styled.div`
  position: relative;
  display: grid;
  grid-column-gap: 16px;
  align-items: center;
  grid-template-columns: 1.5fr 1fr 0.7fr 0.7fr 0.6fr;

  @media ${device.tabletMax} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`

const Name = styled.span`
  font-size: 14px;
  width: 100%;
  text-align: start;
  color: ${colors.thirdBlue};
  font-weight: 600;
  cursor: pointer;
`

const Parameter = styled.div`
  font-size: 12px;
  flex-grow: 1;
  width: 100%;
  display: flex;
  align-items: center;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.red} !important;

  &:hover {
    background-color: ${colors.red} !important;
  }
`

interface UserItemProps {
  user: ManagedUser
  onEditItem: (user: ManagedUser) => void
  onArchiveItem: (id: number) => void
  onRestoreItem: (id: number) => void
  currentUserId?: number
}

export const UserItem: FC<UserItemProps> = ({
  user,
  onEditItem,
  onArchiveItem,
  onRestoreItem,
  currentUserId,
}) => {
  if (!user) {
    return null
  }

  const { id, email, firstName, lastName, phone, role, status } = user

  const fullName = `${firstName || ''} ${lastName || ''}`.trim() || email
  const isSelf = id === currentUserId
  const isArchived = status === UserStatus.ARCHIVED

  return (
    <Container>
      <ParametersContainer>
        <Name onClick={() => onEditItem(user)} data-testid='user-item-name'>
          {fullName}
        </Name>
        <Parameter>{getValueOrFallback(email)}</Parameter>
        <Parameter>{getValueOrFallback(phone ?? undefined)}</Parameter>
        <Parameter>
          <Badge color={isArchived ? 'gray' : 'blue'} variant='light'>
            {roleLabels[role]}
          </Badge>
        </Parameter>

        <ButtonContainer>
          {isArchived ? (
            <Button
              size='sm'
              color='green'
              variant='light'
              onClick={() => onRestoreItem(id)}
              data-testid='restore-user-button'
            >
              Восстановить
            </Button>
          ) : (
            <StyledButton
              onClick={() => onArchiveItem(id)}
              disabled={isSelf}
              tooltiptext={
                isSelf ? 'Нельзя удалить себя' : 'Удалить пользователя'
              }
              data-testid='delete-user-button'
            >
              <Trash />
            </StyledButton>
          )}
        </ButtonContainer>
      </ParametersContainer>
    </Container>
  )
}
